// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

// ─────────────────────────────────────────────────────────────
// COMPLETE APP PORTFOLIO — 21 Products
// ─────────────────────────────────────────────────────────────
const APPS_DATA = [
  { name: 'CropPatrol', brand: 'CropPatrol', vertical: 'Agriculture', url: 'https://croppatrol.pages.dev',
    features: ['GPS field mapping & crop tracking', 'Equipment fleet management', 'Crew scheduling & payroll', 'Weather integration & alerts', 'Harvest yield analytics'],
    market: 'North American farms with 5+ employees',
    advantage: 'Only ag platform combining field ops, crew management, and financial tracking in one tool',
    tam: '$8.2B', color: '#22C55E' },
  { name: 'Forge Fleet', brand: 'Haul Logic', vertical: 'Trucking & Logistics', url: 'https://forge-fleet.pages.dev',
    features: ['Real-time fleet GPS tracking', 'Driver hour-of-service logs', 'Load dispatch & routing', 'Fuel management & analytics', 'DOT compliance tools'],
    market: 'Small-to-mid trucking companies (5-100 trucks)',
    advantage: 'Purpose-built for owner-operators and small fleets, not bloated enterprise software',
    tam: '$12.4B', color: '#3B82F6' },
  { name: 'Directional Ops', brand: 'DirectionalOps', vertical: 'Oil & Gas', url: 'https://directional-ops.pages.dev',
    features: ['Rig operations dashboard', 'Directional drilling data tracking', 'Safety compliance & reporting', 'Equipment maintenance scheduling', 'Crew rotation management'],
    market: 'Directional drilling companies and service providers',
    advantage: 'Niche tool for directional drilling — no competitors building specifically for this segment',
    tam: '$4.7B', color: '#F97316' },
  { name: 'LotSync', brand: 'LotSync', vertical: 'Auto Dealership', url: 'https://lotsync.pages.dev',
    features: ['Vehicle inventory management', 'Sales pipeline & CRM', 'Trade-in appraisal tools', 'Financing calculator', 'Lot analytics & aging reports'],
    market: 'Independent used car dealerships',
    advantage: 'Modern UI vs legacy dealer management systems, fraction of the cost',
    tam: '$6.1B', color: '#EF4444' },
  { name: 'NurseVault', brand: 'NurseVault', vertical: 'Healthcare (US)', url: 'https://nursevault.pages.dev',
    features: ['Shift scheduling & swap management', 'Patient assignment tracking', 'Medication administration logs', 'HIPAA-compliant workflows', 'Overtime & fatigue monitoring'],
    market: 'Hospitals, clinics, nursing homes in the US',
    advantage: 'Built by healthcare workers, not enterprise software companies',
    tam: '$18.3B', color: '#EC4899' },
  { name: 'NurseVault CA', brand: 'NurseVault CA', vertical: 'Healthcare (Canada)', url: 'https://nursevault-ca.pages.dev',
    features: ['Provincial health regulation compliance', 'Bilingual (EN/FR) support', 'Canadian payroll integration', 'Provincial licensing tracking', 'Union schedule management'],
    market: 'Canadian healthcare facilities',
    advantage: 'Only nursing platform built specifically for Canadian provincial regulations',
    tam: '$3.8B', color: '#F472B6' },
  { name: 'Vantage Ops', brand: 'Vantage Ops', vertical: 'Field Service (6 verticals)', url: 'https://vantage-ops.pages.dev',
    features: ['Job creation & dispatch', 'Customer management & CRM', 'Quote generation & invoicing', '6 industry vertical modules', 'Technician mobile views'],
    market: 'Field service businesses: HVAC, plumbing, electrical, garage doors, appliance repair, general contractors',
    advantage: 'One platform, 6 industry verticals — unprecedented flexibility',
    tam: '$9.5B', color: '#8B5CF6' },
  { name: 'BudLedger', brand: 'BudLedger', vertical: 'Cannabis', url: 'https://budledger.pages.dev',
    features: ['Seed-to-sale tracking', 'Dispensary POS system', 'State compliance reporting', 'Lab testing integration', 'Inventory & batch management'],
    market: 'Licensed cannabis cultivators and dispensaries',
    advantage: 'Full vertical integration from grow room to point of sale',
    tam: '$5.2B', color: '#14B8A6' },
  { name: 'OreSync', brand: 'OreSync', vertical: 'Mining', url: 'https://ironledger.pages.dev',
    features: ['Mine site operations dashboard', 'Safety incident reporting', 'Equipment lifecycle tracking', 'Production yield analytics', 'Environmental compliance'],
    market: 'Small-to-mid mining operations',
    advantage: 'Affordable alternative to enterprise mining software (SAP, Oracle)',
    tam: '$7.6B', color: '#A78BFA' },
  { name: 'VacTraq', brand: 'VacTraq', vertical: 'Vacuum Truck Services', url: 'https://haulsync.pages.dev',
    features: ['Truck dispatch & scheduling', 'Ticket management & billing', 'Load tracking & volumes', 'Customer account management', 'Route optimization'],
    market: 'Vac truck and industrial cleaning companies',
    advantage: 'First purpose-built platform for vacuum truck operations',
    tam: '$2.1B', color: '#06B6D4' },
  { name: 'GlowDesk', brand: 'GlowDesk', vertical: 'Beauty & Salon', url: 'https://glowdesk.pages.dev',
    features: ['Online booking system', 'Client profile & history', 'Stylist scheduling & commission', 'Product inventory', 'Revenue analytics'],
    market: 'Hair salons, spas, beauty studios',
    advantage: 'Modern, Instagram-generation UX vs dated salon software',
    tam: '$11.7B', color: '#FB7185' },
  { name: 'ClosingDesk', brand: 'ClosingDesk', vertical: 'Real Estate', url: 'https://closingdesk.pages.dev',
    features: ['Property listing management', 'Buyer/seller CRM', 'Transaction pipeline tracking', 'Document management', 'Commission calculator'],
    market: 'Independent real estate agents and brokerages',
    advantage: 'Streamlined closing workflow vs bloated MLS tools',
    tam: '$14.2B', color: '#FBBF24' },
  { name: 'WrenchSync', brand: 'WrenchSync', vertical: 'Auto Repair', url: 'https://wrenchsync.pages.dev',
    features: ['Work order management', 'Parts inventory & ordering', 'Customer estimates & approvals', 'Technician time tracking', 'Vehicle service history'],
    market: 'Independent auto repair shops',
    advantage: 'Built for independent shops, not franchise dealerships',
    tam: '$5.8B', color: '#EAB308' },
  { name: 'TreadOps', brand: 'TreadOps', vertical: 'Tire Shop', url: 'https://treadops.pages.dev',
    features: ['Tire inventory by size/brand', 'Installation scheduling', 'Fleet account management', 'Seasonal changeover tracking', 'Supplier ordering'],
    market: 'Tire retailers and installation shops',
    advantage: 'Only platform built exclusively for tire shop operations',
    tam: '$1.9B', color: '#78716C' },
  { name: 'HerdLedger', brand: 'HerdLedger', vertical: 'Cattle & Ranch', url: 'https://herdledger.pages.dev',
    features: ['Individual animal tracking', 'Breeding & calving records', 'Pasture rotation management', 'Veterinary health logs', 'Market-ready analytics'],
    market: 'Cattle ranchers and feedlot operators',
    advantage: 'Modern alternative to paper records and spreadsheets',
    tam: '$3.4B', color: '#A3E635' },
  { name: 'VitaSync', brand: 'VitaSync', vertical: 'Wellness & Holistic', url: 'https://vitasync.pages.dev',
    features: ['Client session booking', 'Treatment plan tracking', 'Practitioner scheduling', 'Insurance billing', 'Progress documentation'],
    market: 'Massage therapists, chiropractors, acupuncturists, naturopaths',
    advantage: 'Designed for solo practitioners and small wellness practices',
    tam: '$6.9B', color: '#34D399' },
  { name: 'PolicyForge', brand: 'PolicyForge', vertical: 'Insurance', url: 'https://policyforge.pages.dev',
    features: ['Policy portfolio management', 'Client renewal tracking', 'Commission calculation', 'Quote comparison tools', 'Compliance documentation'],
    market: 'Independent insurance brokers',
    advantage: 'Built for brokers, not carriers — independent-first approach',
    tam: '$10.3B', color: '#38BDF8' },
  { name: 'MT Forge', brand: 'MT Forge', vertical: 'Internal Operations', url: 'https://mt-forge-internal.pages.dev',
    features: ['Team task management', 'CRM & deal pipeline', 'Meeting notes & call logs', 'KPI dashboards', 'Anonymous idea board'],
    market: 'Internal use — Mt Forge team',
    advantage: 'Dogfooding our own platform for internal operations',
    tam: 'Internal', color: '#4F46E5' },
  { name: 'StageVault', brand: 'StageVault', vertical: 'Events & Venues', url: 'https://stagevault.pages.dev',
    features: ['Venue booking calendar', 'Event setup checklists', 'Vendor coordination', 'Floor plan designer', 'Client invoicing'],
    market: 'Event venues, conference centers, banquet halls',
    advantage: 'Combines venue management with vendor coordination in one platform',
    tam: '$4.1B', color: '#C084FC' },
  { name: 'BrodaFarms', brand: 'CropPatrol', vertical: 'Agriculture (Custom)', url: 'https://brodafarms.pages.dev',
    features: ['Custom Broda Farms operations', 'Kamsack, Saskatchewan focused', 'Field & crew management', 'Equipment tracking', 'Harvest reporting'],
    market: 'Broda Farms — custom deployment',
    advantage: 'White-label proof of concept — same platform, custom branding',
    tam: 'White-label', color: '#65A30D' },
  { name: 'IronLedger', brand: 'IronLedger', vertical: 'Overhead Doors', url: 'https://ironledger.pages.dev',
    features: ['Job tracking & scheduling', 'Service call management', 'Parts inventory', 'Customer CRM', 'Invoice generation'],
    market: 'Island Overhead Doors — custom deployment',
    advantage: 'Another white-label proof — shows platform versatility',
    tam: 'White-label', color: '#78716C' },
];

// ─────────────────────────────────────────────────────────────
// TALKING POINTS DATA
// ─────────────────────────────────────────────────────────────
const TALKING_POINTS = [
  {
    id: 'platform',
    icon: '\u{1F3D7}',
    title: 'The Platform Story',
    subtitle: 'One Architecture, 21 Industries',
    points: [
      'We didn\'t build 21 separate products — we built a platform that can be configured for any service-based industry in weeks, not months.',
      'Each new vertical leverages 80% shared code. The remaining 20% is industry-specific configuration, terminology, and workflow logic.',
      'This means our cost to enter a new vertical is a fraction of what a single-product competitor spends building their one tool.',
      'We\'ve proven the model across agriculture, healthcare, oil & gas, cannabis, mining, auto, beauty, real estate, insurance, events, and more.',
      'The architecture is designed for rapid white-labeling — as proven by our Broda Farms and Island Overhead Doors deployments.',
      'Every app shares the same authentication, onboarding, NDA, and tutorial pipeline — ensuring consistent security and user experience.',
    ],
  },
  {
    id: 'tech',
    icon: '\u{26A1}',
    title: 'Technology Advantage',
    subtitle: 'Modern Stack, Edge Deployment',
    points: [
      'React frontend delivers native-app-quality user experience in the browser — no app store required, instant updates.',
      'Supabase backend provides PostgreSQL database, real-time subscriptions, row-level security, and built-in authentication.',
      'Deployed on Cloudflare\'s global edge network — sub-50ms load times worldwide with zero server management.',
      'Total infrastructure cost per app is under $20/month regardless of user count, thanks to edge computing and serverless architecture.',
      'No DevOps team needed. A single developer can deploy, update, and maintain all 21 applications.',
      'The tech stack was chosen for maximum leverage: smallest team, most output, lowest cost, fastest iteration.',
      'Mobile apps built with React Native / Expo (SDK 50) share component logic with web apps.',
    ],
  },
  {
    id: 'market',
    icon: '\u{1F4CA}',
    title: 'Market Opportunity',
    subtitle: 'The Long Tail of Small Business Software',
    points: [
      'We target the long tail — small businesses with 2-50 employees that are too small for enterprise software but too complex for spreadsheets.',
      'There are 33.2 million small businesses in the US alone. Most still run on paper, spreadsheets, and disconnected tools.',
      'Enterprise software (SAP, Oracle, Salesforce) prices out small operators. We offer 90% of the functionality at 10% of the cost.',
      'Each vertical we enter has a Total Addressable Market measured in billions — our combined TAM across 15+ verticals exceeds $130B.',
      'The small business SaaS market is growing at 15-20% annually as digital adoption accelerates post-COVID.',
      'Our multi-vertical approach means we\'re not betting on one market. Downturns in oil & gas can be offset by growth in healthcare or cannabis.',
      'White-label deployments (Broda Farms, IronLedger) open an entirely separate revenue stream — licensing the platform to larger operators.',
    ],
  },
  {
    id: 'gtm',
    icon: '\u{1F680}',
    title: 'Go-to-Market Strategy',
    subtitle: 'Ambassador Network + Direct Sales',
    points: [
      'Ambassador sales network — industry insiders who already have relationships in their vertical handle customer acquisition.',
      'Each ambassador focuses on one or two verticals where they have deep domain expertise and existing contacts.',
      'Commission-based compensation aligns incentives: ambassadors earn when customers succeed.',
      'Direct sales team handles inbound leads, demos, and enterprise accounts.',
      'Product-led growth: free trials and demo accounts let prospects experience the platform before committing.',
      'Vertical-specific branding (CropPatrol for agriculture, NurseVault for healthcare, etc.) creates industry credibility.',
      'Each app has its own pitch deck, NDA, and sales collateral tailored to the specific vertical\'s language and pain points.',
      'Land-and-expand: once a business adopts one module, they typically expand to additional features within 90 days.',
    ],
  },
  {
    id: 'ip',
    icon: '\u{1F512}',
    title: 'IP Protection',
    subtitle: 'NDAs, Trade Secrets, Architecture',
    points: [
      'All team members sign comprehensive NDAs before accessing any codebase or internal systems.',
      'Proprietary architecture is protected as trade secret — the platform pattern, configuration system, and deployment pipeline.',
      '21 working, deployed products serve as our functional patent portfolio equivalent — demonstrable prior art across 15+ industries.',
      'The onboarding gate (password change, NDA signature, tutorial) is enforced on every single application — no exceptions.',
      'Source code is never exposed to clients. All deployments are compiled, minified, and served from Cloudflare\'s edge.',
      'Internal communications, meeting notes, and strategic documents are all within our own platform (MT Forge) — not third-party tools.',
      'Legal documents prepared for all verticals including IP assignment agreements, non-compete clauses, and confidentiality provisions.',
    ],
  },
  {
    id: 'team',
    icon: '\u{1F465}',
    title: 'Team & Operations',
    subtitle: '19-Person Distributed Team',
    points: [
      '19 team members across development, sales, operations, and vertical specialists.',
      'Lean team structure: every person wears multiple hats, keeping burn rate low while maintaining velocity.',
      'Remote-first operations — team members across North America with coordination through MT Forge (our internal platform).',
      'Weekly all-hands meetings, daily async updates, and real-time KPI dashboards keep everyone aligned.',
      'The CEO (Ryan Miskulin) maintains hands-on involvement in product, sales, and strategic direction.',
      'Team members include domain experts in agriculture, healthcare, oil & gas, cannabis, and field services.',
      'Internal tools (MT Forge) serve as proof that we eat our own cooking.',
    ],
  },
  {
    id: 'financials',
    icon: '\u{1F4B0}',
    title: 'Revenue Model & Unit Economics',
    subtitle: 'SaaS Subscription, Low Churn',
    points: [
      'SaaS subscription model: $49-$299/month per business depending on vertical and tier.',
      'Three tiers per vertical: Starter (basic ops), Professional (full suite), Enterprise (custom + API access).',
      'Target blended ARPU of $149/month across all verticals — $1,788 annually per customer.',
      'Infrastructure cost per customer is under $2/month thanks to Cloudflare edge deployment and Supabase efficiency.',
      'Gross margins projected at 95%+ at scale — pure software with minimal per-customer cost.',
      'Customer Acquisition Cost (CAC) reduced by ambassador model — pay commissions only on successful conversions.',
      'Target LTV:CAC ratio of 10:1+ given low churn expectations in operational software (businesses don\'t switch core ops tools).',
      'Revenue diversification: subscription fees, white-label licensing, custom deployment fees, and premium support tiers.',
      'Path to $1M ARR: 560 customers at blended $149/month ARPU across 21 verticals = ~27 customers per vertical.',
    ],
  },
  {
    id: 'roadmap',
    icon: '\u{1F5FA}',
    title: 'Product Roadmap',
    subtitle: '2026 and Beyond',
    points: [
      'Q1 2026: Complete deployment of all 21 web apps + onboarding pipeline. [DONE]',
      'Q1 2026: Launch mobile apps for top 5 verticals (agriculture, healthcare, trucking, field service, cannabis).',
      'Q2 2026: Integrate payment processing (Stripe) for in-app invoicing across all verticals.',
      'Q2 2026: Launch ambassador recruitment campaign — target 50 ambassadors across 15 verticals.',
      'Q3 2026: AI-powered features: smart scheduling, predictive analytics, automated compliance checks.',
      'Q3 2026: API marketplace — let third-party developers build integrations for specific verticals.',
      'Q4 2026: International expansion — localized versions for UK, Australia, and EU markets.',
      '2027: Platform licensing program — enable other software companies to build on our architecture.',
      '2027: Pursue strategic partnerships with industry associations in each vertical.',
    ],
  },
  {
    id: 'competitive',
    icon: '\u{1F3AF}',
    title: 'Competitive Landscape',
    subtitle: 'Why We Win',
    points: [
      'No competitor operates across 15+ verticals with a shared platform architecture. We\'re creating a new category.',
      'Vertical-specific competitors (e.g., Jobber for field service, Jane App for wellness) only address one market each.',
      'Horizontal platforms (Salesforce, HubSpot) require expensive customization and don\'t understand vertical workflows.',
      'Enterprise solutions (SAP, Oracle) are priced for Fortune 500 companies, not 10-person businesses.',
      'Our cost to enter a new vertical is 10-20x lower than a competitor building from scratch.',
      'Speed advantage: we can launch a new vertical in 2-4 weeks. Competitors need 6-12 months.',
      'Multi-vertical presence creates cross-selling opportunities and data insights competitors can\'t match.',
      'The moat deepens with every vertical we add — the platform becomes more valuable and harder to replicate.',
    ],
  },
  {
    id: 'risks',
    icon: '\u{26A0}',
    title: 'Risks & Mitigations',
    subtitle: 'Transparent Assessment',
    points: [
      'RISK: Spreading too thin across verticals. MITIGATION: Ambassador model means we don\'t need internal expertise in every vertical.',
      'RISK: Larger competitor enters our space. MITIGATION: Our speed and cost advantage means we\'ll always be first and cheapest.',
      'RISK: Customer churn. MITIGATION: Operational software is sticky — businesses don\'t switch core tools easily.',
      'RISK: Technical debt from rapid development. MITIGATION: Shared architecture means fixes propagate to all apps simultaneously.',
      'RISK: Regulatory changes in specific verticals (cannabis, healthcare). MITIGATION: Multi-vertical diversification limits exposure.',
      'RISK: Key person dependency. MITIGATION: Documented architecture, growing team, and operational playbooks.',
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// VERTICAL TAM DATA (for the market chart)
// ─────────────────────────────────────────────────────────────
const VERTICAL_TAMS = [
  { vertical: 'Healthcare (US+CA)', tam: 22.1 },
  { vertical: 'Real Estate', tam: 14.2 },
  { vertical: 'Trucking & Logistics', tam: 12.4 },
  { vertical: 'Beauty & Salon', tam: 11.7 },
  { vertical: 'Insurance', tam: 10.3 },
  { vertical: 'Field Service (6v)', tam: 9.5 },
  { vertical: 'Agriculture', tam: 8.2 },
  { vertical: 'Mining', tam: 7.6 },
  { vertical: 'Wellness', tam: 6.9 },
  { vertical: 'Auto Dealership', tam: 6.1 },
  { vertical: 'Auto Repair', tam: 5.8 },
  { vertical: 'Cannabis', tam: 5.2 },
  { vertical: 'Oil & Gas', tam: 4.7 },
  { vertical: 'Events & Venues', tam: 4.1 },
  { vertical: 'Cattle & Ranch', tam: 3.4 },
  { vertical: 'Vac Truck', tam: 2.1 },
  { vertical: 'Tire Shop', tam: 1.9 },
];

// ─────────────────────────────────────────────────────────────
// TIMELINE DATA
// ─────────────────────────────────────────────────────────────
const TIMELINE = [
  { date: 'Q3 2025', event: 'Company founded. Architecture design begins.', status: 'done' },
  { date: 'Q4 2025', event: 'First 5 apps built and tested (Agriculture, Trucking, Oil & Gas, Healthcare, Field Service).', status: 'done' },
  { date: 'Jan 2026', event: 'Expanded to 15 apps. Team grows to 19 members. Supabase auth + onboarding pipeline complete.', status: 'done' },
  { date: 'Feb 2026', event: 'All 21 apps deployed and live. Meeting prep for team all-hands. White-label proofs delivered.', status: 'current' },
  { date: 'Mar 2026', event: 'Mobile app launch for top 5 verticals. Ambassador recruitment begins.', status: 'upcoming' },
  { date: 'Q2 2026', event: 'Stripe integration. First paying customers across 10+ verticals. Ambassador network at 25+.', status: 'upcoming' },
  { date: 'Q3 2026', event: 'AI features. API marketplace. Revenue target: $50K MRR.', status: 'upcoming' },
  { date: 'Q4 2026', event: 'International expansion. Platform licensing pilot. Revenue target: $100K MRR.', status: 'upcoming' },
  { date: '2027', event: 'Series A readiness. 1,000+ customers. Full international presence.', status: 'upcoming' },
];

// ─────────────────────────────────────────────────────────────
// CASE STUDIES
// ─────────────────────────────────────────────────────────────
const CASE_STUDIES = [
  {
    title: 'Broda Farms — White-Label Agriculture',
    location: 'Kamsack, Saskatchewan',
    challenge: 'Large farm operation running on paper records and disconnected spreadsheets. Needed a unified platform for field ops, crew management, and equipment tracking.',
    solution: 'Deployed a white-labeled version of CropPatrol (branded as CropPatrol) with custom branding and farm-specific configurations.',
    result: 'Proof of concept that the platform can be rebranded and deployed for individual enterprises — opening the white-label licensing revenue stream.',
    vertical: 'Agriculture',
    color: '#65A30D',
  },
  {
    title: 'Island Overhead Doors — White-Label Field Service',
    location: 'Service Area',
    challenge: 'Overhead door company managing jobs, parts inventory, and customer relationships across multiple disconnected tools.',
    solution: 'Deployed IronLedger — a white-labeled version of the platform configured for overhead door installation and service.',
    result: 'Demonstrates platform versatility: same architecture serving both agriculture and specialized field service with minimal customization.',
    vertical: 'Overhead Doors',
    color: '#78716C',
  },
  {
    title: 'Vantage Ops — 6 Verticals, One Platform',
    location: 'North America',
    challenge: 'Field service businesses across HVAC, plumbing, electrical, garage doors, appliance repair, and general contracting all need similar tools with industry-specific workflows.',
    solution: 'Built Vantage Ops with switchable vertical modules — one codebase serving 6 distinct field service industries.',
    result: 'Proves the platform\'s configurability within a single deployment. 6x the addressable market with 1.2x the development cost.',
    vertical: 'Field Service',
    color: '#8B5CF6',
  },
  {
    title: 'NurseVault / NurseVault CA — Cross-Border Healthcare',
    location: 'US + Canada',
    challenge: 'Healthcare scheduling software must comply with different regulations in the US (HIPAA, federal labor laws) and Canada (provincial health regulations, bilingual requirements).',
    solution: 'Deployed two versions from the same base: NurseVault (US) and NurseVault CA (Canada) with region-specific compliance, payroll, and language configurations.',
    result: 'Demonstrates the platform\'s ability to handle regulatory variation across borders — a template for future international expansion.',
    vertical: 'Healthcare',
    color: '#EC4899',
  },
];

// ─────────────────────────────────────────────────────────────
// FAQ DATA
// ─────────────────────────────────────────────────────────────
const FAQ_DATA = [
  { q: 'How can such a small team maintain 21 products?', a: 'Shared architecture. 80% of the code is identical across all apps. A bug fix or feature improvement propagates to all 21 products simultaneously. We\'re not maintaining 21 codebases — we\'re maintaining one platform with 21 configurations.' },
  { q: 'Why not focus on one vertical and dominate it?', a: 'Diversification is our moat. No single-vertical competitor can replicate what we\'ve built. If one market cools, others compensate. And each new vertical costs us 10-20x less than a competitor building from scratch.' },
  { q: 'What\'s the pricing model?', a: '$49-$299/month per business. Three tiers: Starter, Professional, Enterprise. Annual prepay discount of 20%. Custom white-label pricing for larger deployments.' },
  { q: 'How do you handle data security?', a: 'Supabase provides row-level security, encrypted connections, and SOC 2 compliance. Each customer\'s data is isolated. All team members sign NDAs. The platform enforces mandatory password changes and NDA signatures during onboarding.' },
  { q: 'What\'s the competitive moat?', a: 'Three layers: (1) The platform architecture itself — years of engineering. (2) Multi-vertical presence — nobody else operates across 15+ industries. (3) Network effects from ambassador relationships in each vertical.' },
  { q: 'Are the mobile apps ready?', a: 'Mobile apps are built with React Native / Expo (SDK 50). Top 5 verticals are targeted for Q1 2026 mobile launch. Web apps work on mobile browsers today.' },
  { q: 'How do you acquire customers?', a: 'Three channels: (1) Ambassador network — industry insiders with existing relationships. (2) Direct sales team for inbound leads. (3) Product-led growth through free trials and demo accounts.' },
  { q: 'What\'s the burn rate?', a: 'Extremely lean. Infrastructure costs under $500/month total for all 21 apps. Team is commission-based with minimal fixed overhead. Runway extends well into 2027 at current spend.' },
  { q: 'Why Cloudflare for deployment?', a: 'Zero-config global CDN, sub-50ms load times worldwide, free for our usage tier, and automatic SSL. We deploy all 21 apps for essentially zero infrastructure cost.' },
  { q: 'What about regulatory compliance?', a: 'Each vertical has industry-specific compliance built in: HIPAA for healthcare, DOT for trucking, state compliance for cannabis, provincial regulations for Canadian healthcare, environmental compliance for mining.' },
  { q: 'Can customers request custom features?', a: 'Enterprise tier includes custom feature requests. For white-label deployments, we build custom configurations on top of the shared platform. Custom work is charged as one-time project fees plus ongoing subscription.' },
  { q: 'What\'s the exit strategy?', a: 'Multiple paths: (1) Acquisition by a horizontal SaaS platform wanting vertical presence. (2) Acquisition by a PE firm building a vertical SaaS portfolio. (3) IPO once reaching $50M+ ARR. (4) Continue as a profitable private company.' },
];

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const S = {
  page: { padding: '0 0 80px 0' },
  hero: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
    border: '1px solid #334155',
    borderRadius: 16,
    padding: '48px 40px',
    marginBottom: 32,
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-block',
    background: 'rgba(79,70,229,0.2)',
    border: '1px solid rgba(79,70,229,0.4)',
    borderRadius: 20,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 600,
    color: '#818CF8',
    marginBottom: 16,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  heroTitle: { fontSize: 36, fontWeight: 800, color: '#f1f5f9', margin: '0 0 8px 0', lineHeight: 1.2 },
  heroSubtitle: { fontSize: 18, color: '#94a3b8', margin: '0 0 24px 0', lineHeight: 1.5 },
  heroMeta: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  heroMetaItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  heroMetaLabel: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 },
  heroMetaValue: { fontSize: 15, color: '#e2e8f0', fontWeight: 600 },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 },
  statBox: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: '20px 16px',
    textAlign: 'center',
  },
  statNumber: { fontSize: 32, fontWeight: 800, color: '#4F46E5', marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 },

  section: { marginBottom: 40 },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    cursor: 'pointer',
    userSelect: 'none',
  },
  sectionTitle: { fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  sectionSubtitle: { fontSize: 13, color: '#64748b', marginTop: 4 },
  sectionToggle: {
    fontSize: 12,
    color: '#4F46E5',
    background: 'rgba(79,70,229,0.1)',
    border: '1px solid rgba(79,70,229,0.3)',
    borderRadius: 6,
    padding: '4px 12px',
    cursor: 'pointer',
    fontWeight: 600,
  },

  card: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 },
  cardBrand: {
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    whiteSpace: 'nowrap',
  },
  cardName: { fontSize: 16, fontWeight: 700, color: '#f1f5f9' },
  cardVertical: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  cardBody: { padding: '0 20px 20px 20px' },
  cardDivider: { height: 1, background: '#334155', margin: '0 0 16px 0' },

  featureList: { listStyle: 'none', padding: 0, margin: '0 0 16px 0' },
  featureItem: { padding: '6px 0', fontSize: 13, color: '#cbd5e1', display: 'flex', alignItems: 'flex-start', gap: 8 },
  featureDot: { width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0 },

  metaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  metaBox: { background: '#0f172a', borderRadius: 8, padding: '12px 14px' },
  metaLabel: { fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 },
  metaValue: { fontSize: 13, color: '#e2e8f0', lineHeight: 1.5 },

  demoLink: {
    display: 'inline-block',
    background: 'rgba(79,70,229,0.15)',
    border: '1px solid rgba(79,70,229,0.3)',
    color: '#818CF8',
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
  },

  talkingCard: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    marginBottom: 12,
    overflow: 'hidden',
  },
  talkingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 20px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  talkingIcon: { fontSize: 24, flexShrink: 0 },
  talkingTitle: { fontSize: 16, fontWeight: 700, color: '#f1f5f9' },
  talkingSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  talkingBody: { padding: '0 20px 20px 20px' },
  talkingPoint: {
    padding: '10px 0',
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
    borderBottom: '1px solid rgba(51,65,85,0.5)',
  },

  tamBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
  },
  tamLabel: { fontSize: 12, color: '#94a3b8', width: 150, flexShrink: 0, textAlign: 'right' },
  tamBarOuter: { flex: 1, height: 24, background: '#0f172a', borderRadius: 6, overflow: 'hidden', position: 'relative' },
  tamBarInner: { height: '100%', borderRadius: 6, display: 'flex', alignItems: 'center', paddingLeft: 8, transition: 'width 0.5s ease' },
  tamValue: { fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' },

  timelineItem: {
    display: 'flex',
    gap: 20,
    padding: '16px 0',
    borderBottom: '1px solid rgba(51,65,85,0.3)',
  },
  timelineDate: { width: 90, flexShrink: 0, fontSize: 13, fontWeight: 700, color: '#818CF8' },
  timelineDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0, marginTop: 4 },
  timelineText: { fontSize: 14, color: '#cbd5e1', lineHeight: 1.5 },

  caseStudy: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
  },
  caseTitle: { fontSize: 17, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 },
  caseLocation: { fontSize: 12, color: '#64748b', marginBottom: 16 },
  caseSection: { marginBottom: 12 },
  caseSectionLabel: { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
  caseSectionText: { fontSize: 13, color: '#cbd5e1', lineHeight: 1.6 },

  faqCard: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    marginBottom: 8,
    overflow: 'hidden',
  },
  faqQ: {
    padding: '14px 20px',
    fontSize: 14,
    fontWeight: 600,
    color: '#f1f5f9',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  faqA: { padding: '0 20px 16px 20px', fontSize: 13, color: '#94a3b8', lineHeight: 1.6 },

  footer: {
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 12,
    padding: '32px 40px',
    textAlign: 'center',
    marginTop: 48,
  },
  footerTitle: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 },
  footerText: { fontSize: 14, color: '#94a3b8', marginBottom: 4, lineHeight: 1.5 },

  arrow: { fontSize: 16, color: '#64748b', transition: 'transform 0.2s', flexShrink: 0 },
  arrowOpen: { transform: 'rotate(90deg)' },

  quoteBanner: {
    background: 'linear-gradient(90deg, rgba(79,70,229,0.15) 0%, rgba(79,70,229,0.05) 100%)',
    borderLeft: '4px solid #4F46E5',
    borderRadius: '0 12px 12px 0',
    padding: '20px 24px',
    marginBottom: 32,
  },
  quoteText: { fontSize: 18, color: '#e2e8f0', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 8 },
  quoteAuthor: { fontSize: 13, color: '#64748b', fontWeight: 600 },

  gridThree: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 },

  verticalPill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    margin: '2px 4px 2px 0',
  },

  searchBar: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 14,
    color: '#f1f5f9',
    width: '100%',
    marginBottom: 16,
    outline: 'none',
    boxSizing: 'border-box',
  },

  expandAllBtn: {
    background: 'rgba(79,70,229,0.1)',
    border: '1px solid rgba(79,70,229,0.3)',
    color: '#818CF8',
    padding: '6px 14px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

// ─────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────
export default function MeetingBriefPage() {
  const [openApps, setOpenApps] = useState({});
  const [openTalking, setOpenTalking] = useState({});
  const [openFaq, setOpenFaq] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllApps, setShowAllApps] = useState(false);
  const [showTimeline, setShowTimeline] = useState(true);
  const [showCaseStudies, setShowCaseStudies] = useState(true);
  const [showFaq, setShowFaq] = useState(true);
  const [showTam, setShowTam] = useState(true);
  const [activeVerticalFilter, setActiveVerticalFilter] = useState('All');

  const toggleApp = (i) => setOpenApps(p => ({ ...p, [i]: !p[i] }));
  const toggleTalking = (id) => setOpenTalking(p => ({ ...p, [id]: !p[id] }));
  const toggleFaq = (i) => setOpenFaq(p => ({ ...p, [i]: !p[i] }));

  const expandAllApps = () => {
    const allOpen = {};
    APPS_DATA.forEach((_, i) => { allOpen[i] = true; });
    setOpenApps(allOpen);
    setShowAllApps(true);
  };
  const collapseAllApps = () => {
    setOpenApps({});
    setShowAllApps(false);
  };

  const expandAllTalking = () => {
    const allOpen = {};
    TALKING_POINTS.forEach(tp => { allOpen[tp.id] = true; });
    setOpenTalking(allOpen);
  };
  const collapseAllTalking = () => setOpenTalking({});

  const expandAllFaq = () => {
    const allOpen = {};
    FAQ_DATA.forEach((_, i) => { allOpen[i] = true; });
    setOpenFaq(allOpen);
  };
  const collapseAllFaq = () => setOpenFaq({});

  // Unique verticals for filter pills
  const uniqueVerticals = ['All', ...Array.from(new Set(APPS_DATA.map(a => a.vertical)))];

  // Filter apps
  const filteredApps = APPS_DATA.filter(app => {
    const matchesSearch = !searchTerm ||
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.vertical.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.market.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVertical = activeVerticalFilter === 'All' || app.vertical === activeVerticalFilter;
    return matchesSearch && matchesVertical;
  });

  const maxTam = Math.max(...VERTICAL_TAMS.map(v => v.tam));
  const totalTam = VERTICAL_TAMS.reduce((s, v) => s + v.tam, 0);

  return (
    <div style={S.page}>
      {/* ═══════════ PAGE HEADER ═══════════ */}
      <div className="page-header">
        <div>
          <h1>Meeting Brief</h1>
          <p>Comprehensive investor & partner meeting material</p>
        </div>
      </div>

      {/* ═══════════ HERO SECTION ═══════════ */}
      <div style={S.hero}>
        <div style={S.heroBadge}>Confidential — Investor Material</div>
        <h1 style={S.heroTitle}>Mt Forge</h1>
        <p style={S.heroSubtitle}>
          One platform. 21 products. 15+ industries. The operating system for small business.
        </p>
        <div style={S.heroMeta}>
          <div style={S.heroMetaItem}>
            <span style={S.heroMetaLabel}>Headquarters</span>
            <span style={S.heroMetaValue}>121 Rainbow Falls Bay, Alberta</span>
          </div>
          <div style={S.heroMetaItem}>
            <span style={S.heroMetaLabel}>Contact</span>
            <span style={S.heroMetaValue}>r.miskulin@mtforge.ai</span>
          </div>
          <div style={S.heroMetaItem}>
            <span style={S.heroMetaLabel}>Founded</span>
            <span style={S.heroMetaValue}>2025</span>
          </div>
          <div style={S.heroMetaItem}>
            <span style={S.heroMetaLabel}>CEO</span>
            <span style={S.heroMetaValue}>Ryan Miskulin</span>
          </div>
        </div>
      </div>

      {/* ═══════════ QUICK STATS PANEL ═══════════ */}
      <div style={S.statsRow}>
        <div style={S.statBox}>
          <div style={S.statNumber}>21</div>
          <div style={S.statLabel}>Live Products</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statNumber}>15+</div>
          <div style={S.statLabel}>Industry Verticals</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statNumber}>19</div>
          <div style={S.statLabel}>Team Members</div>
        </div>
        <div style={S.statBox}>
          <div style={{ ...S.statNumber, fontSize: 24 }}>$136B+</div>
          <div style={S.statLabel}>Combined TAM</div>
        </div>
        <div style={S.statBox}>
          <div style={S.statNumber}>2</div>
          <div style={S.statLabel}>White-Label Proofs</div>
        </div>
        <div style={S.statBox}>
          <div style={{ ...S.statNumber, color: '#22C55E' }}>$49-299</div>
          <div style={S.statLabel}>/Month Per Customer</div>
        </div>
        <div style={S.statBox}>
          <div style={{ ...S.statNumber, color: '#EAB308' }}>80%</div>
          <div style={S.statLabel}>Shared Code Base</div>
        </div>
        <div style={S.statBox}>
          <div style={{ ...S.statNumber, color: '#EC4899' }}>95%+</div>
          <div style={S.statLabel}>Gross Margin Target</div>
        </div>
      </div>

      {/* ═══════════ EXECUTIVE SUMMARY ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Executive Summary</h2>
            <div style={S.sectionSubtitle}>The big picture in 60 seconds</div>
          </div>
        </div>

        <div style={S.quoteBanner}>
          <div style={S.quoteText}>
            "We didn't build 21 separate products — we built a platform that can be configured for any service-based industry in weeks, not months."
          </div>
          <div style={S.quoteAuthor}>— The Platform Thesis</div>
        </div>

        <div style={S.gridThree}>
          <div style={{ ...S.card, padding: 20, marginBottom: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#818CF8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>What We Do</div>
            <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.7 }}>
              Mt Forge builds industry-specific SaaS applications for small businesses. Each app is tailored to a specific vertical — agriculture, healthcare, trucking, cannabis, mining, beauty, real estate, and more — but all share a common platform architecture. This means we can launch a new vertical in 2-4 weeks instead of 6-12 months, at a fraction of the cost of building from scratch.
            </div>
          </div>
          <div style={{ ...S.card, padding: 20, marginBottom: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#22C55E', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Why It Matters</div>
            <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.7 }}>
              33.2 million small businesses in the US alone run on paper, spreadsheets, and disconnected tools. Enterprise software is too expensive and too complex. We offer 90% of the functionality at 10% of the cost, built specifically for 2-50 employee businesses that are too small for SAP but too complex for a spreadsheet.
            </div>
          </div>
          <div style={{ ...S.card, padding: 20, marginBottom: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#EAB308', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>The Opportunity</div>
            <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.7 }}>
              Our combined addressable market across 15+ verticals exceeds $136 billion. The multi-vertical approach means we're not dependent on any single industry. With an ambassador sales network handling acquisition and a platform that scales at near-zero marginal cost, the path to profitability is clear: 27 customers per vertical gets us to $1M ARR.
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ TECH STACK OVERVIEW ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Technology Stack</h2>
            <div style={S.sectionSubtitle}>Modern, lean, infinitely scalable</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {[
            { label: 'Frontend', value: 'React', detail: 'Component-based UI, single-page app, native-quality experience', color: '#61DAFB' },
            { label: 'Routing', value: 'React Router', detail: 'Client-side routing, protected routes, onboarding gates', color: '#CA4245' },
            { label: 'Backend', value: 'Supabase', detail: 'PostgreSQL, real-time subs, row-level security, built-in auth', color: '#3ECF8E' },
            { label: 'Hosting', value: 'Cloudflare Pages', detail: 'Global edge network, sub-50ms loads, zero server management', color: '#F6821F' },
            { label: 'Mobile', value: 'React Native / Expo', detail: 'SDK 50, shared component logic with web, iOS + Android', color: '#000' },
            { label: 'Deploy', value: 'Wrangler CLI', detail: 'One-command deployment, instant rollbacks, branch previews', color: '#F6821F' },
            { label: 'Auth', value: 'Supabase Auth', detail: 'Email/password, magic links, OAuth, MFA support', color: '#3ECF8E' },
            { label: 'Cost', value: '~$20/app/month', detail: 'Total infrastructure regardless of user count', color: '#22C55E' },
          ].map((tech, i) => (
            <div key={i} style={{ ...S.card, padding: 16, marginBottom: 0 }}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6 }}>{tech.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: tech.color, marginBottom: 6 }}>{tech.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{tech.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ APP PORTFOLIO DEEP DIVE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>App Portfolio — Deep Dive ({APPS_DATA.length} Products)</h2>
            <div style={S.sectionSubtitle}>Click any app to expand full details</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.expandAllBtn} onClick={showAllApps ? collapseAllApps : expandAllApps}>
              {showAllApps ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search apps by name, brand, vertical, or market..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={S.searchBar}
        />

        {/* Vertical Filter Pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
          {uniqueVerticals.map(v => (
            <button
              key={v}
              onClick={() => setActiveVerticalFilter(v)}
              style={{
                ...S.verticalPill,
                background: activeVerticalFilter === v ? 'rgba(79,70,229,0.3)' : 'rgba(51,65,85,0.3)',
                border: `1px solid ${activeVerticalFilter === v ? '#4F46E5' : '#334155'}`,
                color: activeVerticalFilter === v ? '#818CF8' : '#94a3b8',
                cursor: 'pointer',
              }}
            >
              {v}
            </button>
          ))}
        </div>

        {/* App count badge */}
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Showing {filteredApps.length} of {APPS_DATA.length} apps
        </div>

        {/* App Accordion Cards */}
        {filteredApps.map((app, idx) => {
          const globalIdx = APPS_DATA.indexOf(app);
          const isOpen = openApps[globalIdx];
          return (
            <div key={app.name} style={{ ...S.card, borderColor: isOpen ? app.color : '#334155' }}>
              <div style={S.cardHeader} onClick={() => toggleApp(globalIdx)}>
                <div style={S.cardHeaderLeft}>
                  <span style={{ ...S.cardBrand, background: `${app.color}22`, color: app.color, border: `1px solid ${app.color}44` }}>
                    {app.brand}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={S.cardName}>{app.name}</div>
                    <div style={S.cardVertical}>{app.vertical}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {app.tam && app.tam !== 'Internal' && app.tam !== 'White-label' && (
                    <span style={{ fontSize: 12, color: '#22C55E', fontWeight: 700 }}>TAM: {app.tam}</span>
                  )}
                  {(app.tam === 'Internal' || app.tam === 'White-label') && (
                    <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, padding: '2px 8px', background: '#0f172a', borderRadius: 4 }}>{app.tam}</span>
                  )}
                  <span style={{ ...S.arrow, ...(isOpen ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
                </div>
              </div>

              {isOpen && (
                <div style={S.cardBody}>
                  <div style={S.cardDivider} />

                  {/* Features */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={S.metaLabel}>Key Features</div>
                    <ul style={S.featureList}>
                      {app.features.map((f, fi) => (
                        <li key={fi} style={S.featureItem}>
                          <span style={{ ...S.featureDot, background: app.color }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Meta Grid */}
                  <div style={S.metaGrid}>
                    <div style={S.metaBox}>
                      <div style={S.metaLabel}>Target Market</div>
                      <div style={S.metaValue}>{app.market}</div>
                    </div>
                    <div style={S.metaBox}>
                      <div style={S.metaLabel}>Competitive Advantage</div>
                      <div style={S.metaValue}>{app.advantage}</div>
                    </div>
                    <div style={S.metaBox}>
                      <div style={S.metaLabel}>Revenue Model</div>
                      <div style={S.metaValue}>SaaS subscription: $49-$299/month. Three tiers — Starter, Professional, Enterprise. Annual prepay discount available.</div>
                    </div>
                    <div style={S.metaBox}>
                      <div style={S.metaLabel}>TAM (Total Addressable Market)</div>
                      <div style={S.metaValue}>{app.tam === 'Internal' ? 'Internal tool — not customer-facing' : app.tam === 'White-label' ? 'White-label deployment — licensing revenue model' : `${app.tam} globally in the ${app.vertical.toLowerCase()} software market`}</div>
                    </div>
                  </div>

                  {/* Demo Link */}
                  <a href={app.url} target="_blank" rel="noopener noreferrer" style={S.demoLink}>
                    Open Live Demo &rarr; {app.url}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ═══════════ TAM VISUALIZATION ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader} onClick={() => setShowTam(!showTam)}>
          <div>
            <h2 style={S.sectionTitle}>Market Opportunity — TAM by Vertical</h2>
            <div style={S.sectionSubtitle}>Combined addressable market: ${totalTam.toFixed(1)}B</div>
          </div>
          <span style={{ ...S.arrow, ...(showTam ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
        </div>

        {showTam && (
          <div style={{ ...S.card, padding: 24, marginBottom: 0 }}>
            {VERTICAL_TAMS.map((v, i) => (
              <div key={i} style={S.tamBar}>
                <span style={S.tamLabel}>{v.vertical}</span>
                <div style={S.tamBarOuter}>
                  <div style={{ ...S.tamBarInner, width: `${(v.tam / maxTam) * 100}%`, background: `linear-gradient(90deg, #4F46E5, #818CF8)` }}>
                    <span style={S.tamValue}>${v.tam}B</span>
                  </div>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16, padding: 16, background: '#0f172a', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 4 }}>Total Combined TAM</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#4F46E5' }}>${totalTam.toFixed(1)}B</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Even capturing 0.1% = ${(totalTam * 0.001).toFixed(0)}M+ revenue opportunity</div>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════ TALKING POINTS ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Talking Points</h2>
            <div style={S.sectionSubtitle}>{TALKING_POINTS.length} sections of investor-ready talking points</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.expandAllBtn} onClick={Object.keys(openTalking).length > 0 ? collapseAllTalking : expandAllTalking}>
              {Object.keys(openTalking).length > 0 ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
        </div>

        {TALKING_POINTS.map(tp => {
          const isOpen = openTalking[tp.id];
          return (
            <div key={tp.id} style={{ ...S.talkingCard, borderColor: isOpen ? '#4F46E5' : '#334155' }}>
              <div style={S.talkingHeader} onClick={() => toggleTalking(tp.id)}>
                <span style={S.talkingIcon}>{tp.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={S.talkingTitle}>{tp.title}</div>
                  <div style={S.talkingSubtitle}>{tp.subtitle}</div>
                </div>
                <span style={{ fontSize: 12, color: '#64748b', marginRight: 8 }}>{tp.points.length} points</span>
                <span style={{ ...S.arrow, ...(isOpen ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
              </div>
              {isOpen && (
                <div style={S.talkingBody}>
                  {tp.points.map((point, pi) => (
                    <div key={pi} style={{ ...S.talkingPoint, borderBottom: pi < tp.points.length - 1 ? '1px solid rgba(51,65,85,0.5)' : 'none' }}>
                      <span style={{ color: '#4F46E5', fontWeight: 700, marginRight: 8 }}>{pi + 1}.</span>
                      {point}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ═══════════ CASE STUDIES ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader} onClick={() => setShowCaseStudies(!showCaseStudies)}>
          <div>
            <h2 style={S.sectionTitle}>Case Studies</h2>
            <div style={S.sectionSubtitle}>{CASE_STUDIES.length} proof points demonstrating platform versatility</div>
          </div>
          <span style={{ ...S.arrow, ...(showCaseStudies ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
        </div>

        {showCaseStudies && (
          <div style={S.gridThree}>
            {CASE_STUDIES.map((cs, i) => (
              <div key={i} style={S.caseStudy}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ ...S.verticalPill, background: `${cs.color}22`, color: cs.color, border: `1px solid ${cs.color}44` }}>{cs.vertical}</span>
                </div>
                <div style={S.caseTitle}>{cs.title}</div>
                <div style={S.caseLocation}>{cs.location}</div>

                <div style={S.caseSection}>
                  <div style={S.caseSectionLabel}>Challenge</div>
                  <div style={S.caseSectionText}>{cs.challenge}</div>
                </div>
                <div style={S.caseSection}>
                  <div style={S.caseSectionLabel}>Solution</div>
                  <div style={S.caseSectionText}>{cs.solution}</div>
                </div>
                <div style={S.caseSection}>
                  <div style={{ ...S.caseSectionLabel, color: '#22C55E' }}>Result</div>
                  <div style={S.caseSectionText}>{cs.result}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════ COMPANY TIMELINE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader} onClick={() => setShowTimeline(!showTimeline)}>
          <div>
            <h2 style={S.sectionTitle}>Company Timeline</h2>
            <div style={S.sectionSubtitle}>From founding to future milestones</div>
          </div>
          <span style={{ ...S.arrow, ...(showTimeline ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
        </div>

        {showTimeline && (
          <div style={{ ...S.card, padding: 24, marginBottom: 0 }}>
            {TIMELINE.map((t, i) => (
              <div key={i} style={S.timelineItem}>
                <span style={{ ...S.timelineDate, color: t.status === 'current' ? '#22C55E' : t.status === 'done' ? '#818CF8' : '#64748b' }}>
                  {t.date}
                </span>
                <span style={{
                  ...S.timelineDot,
                  background: t.status === 'current' ? '#22C55E' : t.status === 'done' ? '#4F46E5' : '#334155',
                  border: t.status === 'upcoming' ? '2px solid #64748b' : 'none',
                  boxShadow: t.status === 'current' ? '0 0 8px rgba(34,197,94,0.4)' : 'none',
                }} />
                <div>
                  <span style={{ ...S.timelineText, color: t.status === 'current' ? '#f1f5f9' : t.status === 'done' ? '#cbd5e1' : '#64748b', fontWeight: t.status === 'current' ? 600 : 400 }}>
                    {t.event}
                  </span>
                  {t.status === 'done' && <span style={{ marginLeft: 8, fontSize: 11, color: '#4F46E5', fontWeight: 600 }}>COMPLETE</span>}
                  {t.status === 'current' && <span style={{ marginLeft: 8, fontSize: 11, color: '#22C55E', fontWeight: 700 }}>NOW</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════ KEY METRICS TABLE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Key Metrics & Projections</h2>
            <div style={S.sectionSubtitle}>Unit economics and growth targets</div>
          </div>
        </div>
        <div style={{ ...S.card, padding: 0, marginBottom: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Metric', 'Current', 'Q2 2026 Target', 'Q4 2026 Target', '2027 Target'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'left', borderBottom: '1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Live Products', '21', '23', '25', '30+'],
                ['Team Size', '19', '25', '35', '50+'],
                ['Monthly Active Customers', 'Pre-revenue', '100', '500', '1,500+'],
                ['MRR (Monthly Recurring Revenue)', '$0', '$15K', '$75K', '$225K+'],
                ['ARR (Annual Recurring Revenue)', '$0', '$180K', '$900K', '$2.7M+'],
                ['Blended ARPU', 'N/A', '$149/mo', '$149/mo', '$149/mo'],
                ['Infrastructure Cost / Customer', 'N/A', '<$2/mo', '<$2/mo', '<$2/mo'],
                ['Gross Margin', 'N/A', '93%', '95%', '96%'],
                ['Ambassador Network', '0', '25', '50', '100+'],
                ['Verticals Covered', '15+', '17+', '20+', '25+'],
                ['Customer Acquisition Cost', 'N/A', '<$200', '<$150', '<$100'],
                ['LTV:CAC Ratio', 'N/A', '8:1', '10:1', '15:1'],
                ['Churn Rate (Monthly)', 'N/A', '<5%', '<3%', '<2%'],
                ['White-Label Deployments', '2', '5', '10', '20+'],
              ].map((row, ri) => (
                <tr key={ri} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{row[0]}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, color: '#94a3b8' }}>{row[1]}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, color: '#818CF8' }}>{row[2]}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, color: '#22C55E' }}>{row[3]}</td>
                  <td style={{ padding: '10px 16px', fontSize: 13, color: '#FBBF24', fontWeight: 600 }}>{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════ REVENUE MODEL BREAKDOWN ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Revenue Streams</h2>
            <div style={S.sectionSubtitle}>Diversified revenue model</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
          {[
            { stream: 'SaaS Subscriptions', desc: 'Core revenue. $49-$299/month per business. Three tiers per vertical. Annual prepay option at 20% discount.', pct: '70%', color: '#4F46E5' },
            { stream: 'White-Label Licensing', desc: 'Licensing the platform to larger enterprises who want their own branded deployment. One-time setup + ongoing license fee.', pct: '15%', color: '#22C55E' },
            { stream: 'Custom Development', desc: 'Enterprise-tier custom features, integrations, and vertical-specific configurations. Charged as project fees.', pct: '10%', color: '#EAB308' },
            { stream: 'Premium Support', desc: 'Priority support, dedicated account manager, SLA guarantees, custom training. Premium tier add-on.', pct: '5%', color: '#EC4899' },
          ].map((r, i) => (
            <div key={i} style={{ ...S.card, padding: 20, marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{r.stream}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: r.color }}>{r.pct}</div>
              </div>
              <div style={{ height: 4, background: '#0f172a', borderRadius: 2, marginBottom: 12 }}>
                <div style={{ height: '100%', width: r.pct, background: r.color, borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{r.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ PATH TO $1M ARR ═══════════ */}
      <div style={S.section}>
        <div style={{ ...S.quoteBanner, borderLeftColor: '#22C55E' }}>
          <div style={{ ...S.quoteText, fontStyle: 'normal', fontSize: 16 }}>
            Path to $1M ARR: 560 customers at $149/month average across 21 verticals = only ~27 customers per vertical. That's achievable with a single ambassador per vertical closing 2-3 deals per month.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { label: '$1M ARR Target', value: '560 customers', detail: 'Across 21 verticals' },
            { label: 'Per Vertical', value: '~27 customers', detail: 'Highly achievable' },
            { label: 'Blended ARPU', value: '$149/month', detail: '$1,788 annually' },
            { label: 'Ambassador Target', value: '1-2 per vertical', detail: 'Closing 2-3 deals/month' },
            { label: 'Time to $1M ARR', value: '12-18 months', detail: 'From first paying customer' },
          ].map((item, i) => (
            <div key={i} style={{ ...S.card, padding: 16, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>{item.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#22C55E', marginBottom: 4 }}>{item.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{item.detail}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ VERTICAL COVERAGE MAP ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Industry Coverage Map</h2>
            <div style={S.sectionSubtitle}>Every vertical we serve, categorized</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {[
            { category: 'Agriculture & Ranching', apps: ['CropPatrol (CropPatrol)', 'HerdLedger (HerdLedger)', 'BrodaFarms (CropPatrol)'], color: '#22C55E', icon: '\u{1F33E}' },
            { category: 'Healthcare', apps: ['NurseVault (NurseVault)', 'NurseVault CA (NurseVault CA)', 'VitaSync (VitaSync)'], color: '#EC4899', icon: '\u{1FA7A}' },
            { category: 'Energy & Resources', apps: ['Directional Ops (Directional Ops)', 'OreSync (OreSync)', 'VacTraq (VacTraq)'], color: '#F97316', icon: '\u{26CF}' },
            { category: 'Automotive', apps: ['LotSync (LotSync)', 'WrenchSync (WrenchSync)', 'TreadOps (TreadOps)'], color: '#EF4444', icon: '\u{1F697}' },
            { category: 'Transportation', apps: ['Forge Fleet (Haul Logic)'], color: '#3B82F6', icon: '\u{1F69B}' },
            { category: 'Field Service', apps: ['Vantage Ops (Vantage Ops) — 6 sub-verticals', 'IronLedger (Overhead Doors)'], color: '#8B5CF6', icon: '\u{1F527}' },
            { category: 'Lifestyle & Personal Care', apps: ['GlowDesk (GlowDesk)'], color: '#FB7185', icon: '\u{2728}' },
            { category: 'Professional Services', apps: ['ClosingDesk (ClosingDesk)', 'PolicyForge (PolicyForge)'], color: '#FBBF24', icon: '\u{1F4BC}' },
            { category: 'Regulated Industries', apps: ['BudLedger (BudLedger)'], color: '#14B8A6', icon: '\u{1F33F}' },
            { category: 'Events', apps: ['StageVault (StageVault)'], color: '#C084FC', icon: '\u{1F3AA}' },
            { category: 'Internal', apps: ['MT Forge (Internal)'], color: '#4F46E5', icon: '\u{1F3E2}' },
          ].map((cat, i) => (
            <div key={i} style={{ ...S.card, padding: 16, marginBottom: 0, borderLeft: `3px solid ${cat.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 20 }}>{cat.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{cat.category}</span>
              </div>
              {cat.apps.map((app, ai) => (
                <div key={ai} style={{ fontSize: 13, color: '#94a3b8', padding: '3px 0', paddingLeft: 12, borderLeft: `2px solid ${cat.color}33` }}>{app}</div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ INVESTOR FAQ ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader} onClick={() => setShowFaq(!showFaq)}>
          <div>
            <h2 style={S.sectionTitle}>Investor FAQ</h2>
            <div style={S.sectionSubtitle}>{FAQ_DATA.length} frequently asked questions</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {showFaq && (
              <button style={S.expandAllBtn} onClick={(e) => { e.stopPropagation(); Object.keys(openFaq).length > 0 ? collapseAllFaq() : expandAllFaq(); }}>
                {Object.keys(openFaq).length > 0 ? 'Collapse All' : 'Expand All'}
              </button>
            )}
            <span style={{ ...S.arrow, ...(showFaq ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
          </div>
        </div>

        {showFaq && FAQ_DATA.map((faq, i) => {
          const isOpen = openFaq[i];
          return (
            <div key={i} style={{ ...S.faqCard, borderColor: isOpen ? '#4F46E5' : '#334155' }}>
              <div style={S.faqQ} onClick={() => toggleFaq(i)}>
                <span>{faq.q}</span>
                <span style={{ ...S.arrow, ...(isOpen ? S.arrowOpen : {}) }}>{'\u25B6'}</span>
              </div>
              {isOpen && <div style={S.faqA}>{faq.a}</div>}
            </div>
          );
        })}
      </div>

      {/* ═══════════ COMPETITIVE COMPARISON TABLE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Competitive Comparison</h2>
            <div style={S.sectionSubtitle}>How we stack up against categories of competitors</div>
          </div>
        </div>
        <div style={{ ...S.card, padding: 0, marginBottom: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Factor', 'Miskulin Tech', 'Vertical SaaS (Jobber, etc.)', 'Horizontal (Salesforce, HubSpot)', 'Enterprise (SAP, Oracle)'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', borderBottom: '1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Verticals Served', '15+', '1', '0 (horizontal)', '1-3'],
                ['Target Customer Size', '2-50 employees', '1-100 employees', '10-10,000+', '500+'],
                ['Monthly Price', '$49-$299', '$49-$299', '$25-$300+/user', '$5,000+'],
                ['Time to New Vertical', '2-4 weeks', 'N/A (single vertical)', 'Months of customization', '6-12 months'],
                ['Setup Complexity', 'Self-serve (minutes)', 'Self-serve (hours)', 'Consultant required', 'Multi-month implementation'],
                ['Mobile App', 'React Native (shared code)', 'Varies', 'Yes (separate builds)', 'Yes (separate builds)'],
                ['White-Label Option', 'Yes (proven)', 'Rarely', 'No', 'Yes (expensive)'],
                ['Infrastructure Cost', '~$20/app/month', '$500+/month', 'N/A (hosted)', '$10,000+/month'],
              ].map((row, ri) => (
                <tr key={ri} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{row[0]}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#22C55E', fontWeight: 600 }}>{row[1]}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>{row[2]}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>{row[3]}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════ DEPLOYMENT ARCHITECTURE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Deployment Architecture</h2>
            <div style={S.sectionSubtitle}>How a single command deploys globally</div>
          </div>
        </div>
        <div style={{ ...S.card, padding: 24, marginBottom: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, textAlign: 'center' }}>
            {[
              { step: '1', title: 'Code', desc: 'React app with shared platform components + vertical-specific config', color: '#3B82F6' },
              { step: '2', title: 'Build', desc: 'npm run build — optimized, minified, tree-shaken bundle', color: '#8B5CF6' },
              { step: '3', title: 'Deploy', desc: 'npx wrangler pages deploy — single CLI command', color: '#F97316' },
              { step: '4', title: 'Edge', desc: 'Cloudflare distributes to 300+ global data centers', color: '#22C55E' },
              { step: '5', title: 'Live', desc: 'Sub-50ms load times worldwide, auto-SSL, zero maintenance', color: '#4F46E5' },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${s.color}22`, border: `2px solid ${s.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 16, fontWeight: 800, color: s.color }}>{s.step}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, padding: 16, background: '#0f172a', borderRadius: 8, fontFamily: 'monospace', fontSize: 13, color: '#22C55E' }}>
            $ npx wrangler pages deploy build --project-name=croppatrol --branch=main<br />
            <span style={{ color: '#64748b' }}>// One command. 300+ edge locations. Zero config. Repeat for each app.</span>
          </div>
        </div>
      </div>

      {/* ═══════════ ONBOARDING SECURITY PIPELINE ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Security & Onboarding Pipeline</h2>
            <div style={S.sectionSubtitle}>Every app enforces a 4-step onboarding gate</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          {[
            { step: 'Step 1', title: 'Login', desc: 'Email/password authentication via Supabase Auth. Encrypted connections, rate limiting, brute-force protection.', color: '#3B82F6' },
            { step: 'Step 2', title: 'Password Change', desc: 'Mandatory password change on first login. Collects phone number and address for team directory.', color: '#8B5CF6' },
            { step: 'Step 3', title: 'NDA Signature', desc: 'Full NDA displayed and signed electronically. Required — cannot be bypassed. Timestamp and IP recorded.', color: '#EF4444' },
            { step: 'Step 4', title: 'App Tutorial', desc: 'Guided tour of the application. Ensures users understand key features before accessing the platform.', color: '#22C55E' },
          ].map((s, i) => (
            <div key={i} style={{ ...S.card, padding: 20, marginBottom: 0, borderTop: `3px solid ${s.color}` }}>
              <div style={{ fontSize: 10, color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{s.step}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════ QUICK REFERENCE: ALL LIVE URLS ═══════════ */}
      <div style={S.section}>
        <div style={S.sectionHeader}>
          <div>
            <h2 style={S.sectionTitle}>Live Demo URLs</h2>
            <div style={S.sectionSubtitle}>Every deployed product — click to open</div>
          </div>
        </div>
        <div style={{ ...S.card, padding: 20, marginBottom: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
            {APPS_DATA.map((app, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(51,65,85,0.3)' }}>
                <span style={{ ...S.featureDot, background: app.color, width: 8, height: 8 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', minWidth: 160 }}>{app.name}</span>
                <a href={app.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#818CF8', textDecoration: 'none' }}>
                  {app.url}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════ FOOTER ═══════════ */}
      <div style={S.footer}>
        <div style={S.footerTitle}>Mt Forge</div>
        <div style={S.footerText}>121 Rainbow Falls Bay, Alberta</div>
        <div style={S.footerText}>r.miskulin@mtforge.ai</div>
        <div style={{ marginTop: 16, fontSize: 12, color: '#475569' }}>
          This document contains confidential and proprietary information. Distribution without written consent is prohibited.
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#334155' }}>
          Last updated: February 2026
        </div>
      </div>
    </div>
  );
}
